import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { TENANT_MGMT_ROUTES } from '@app/+state/user-data/user.model';

import { minCharacters } from '@app/v2/shared/validators';
import { CommonConstants, DOMAINS } from '@core/common-constants';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  ApplicationCard,
  ControlPageFacade,
  CreateChildTenant,
  CreateTenant,
  UiStateFacade,
  UserDataFacade,
  UserPreferences,
} from '@ra-state';
import {
  ActionButtonStyles,
  DialogService,
  IDialogConfig,
  ITooltipConfig,
  NotificationType,
  RaUiButtonModule,
  RaUiCheckboxModule,
  RaUiDividerModule,
  RaUiFileInputModule,
  RaUiInputModule,
  RaUiMultilineInputModule,
  RaUiTooltipModule,
  TooltipPosition,
} from '@ra-web-tech-ui-toolkit/components';
import * as R from 'remeda';
import { Subscription } from 'rxjs';
import { startWith, tap } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import { openDialogWrapper$ } from '../../../shared/utils';
import { ViewTitleComponent } from '../../../shared/view-title/view-title.component';
@Component({
  selector: 'app-create-organization',
  standalone: true,
  imports: [
    RaUiButtonModule,
    RaUiFileInputModule,
    RaUiInputModule,
    RaUiMultilineInputModule,
    RaUiDividerModule,
    RaUiTooltipModule,
    RaUiCheckboxModule,
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    ViewTitleComponent,
  ],
  styleUrls: ['./create-organization.component.scss'],
  templateUrl: './create-organization.component.html',
  encapsulation: ViewEncapsulation.None,
})
export class CreateOrganizationComponent implements OnInit, OnDestroy {
  private readonly formBuilder = inject(UntypedFormBuilder);
  private readonly router = inject(Router);
  private readonly userDataFacade = inject(UserDataFacade);
  private readonly uiStateFacade = inject(UiStateFacade);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly dialogSvc = inject(DialogService);
  private readonly controlPageFacade = inject(ControlPageFacade);
  private readonly translate = inject(TranslateService);

  @Input() isSubOrgCreation: boolean = false;
  @ViewChild('orgCreatedSuccess') orgCreatedSuccess: TemplateRef<any>;

  readonly tooltipConfig: ITooltipConfig = { position: TooltipPosition.Right };
  openDialogWrapper$ = openDialogWrapper$;
  private readonly DASHBOARD_PATH = '/dashboard';
  createTenantForm: UntypedFormGroup;
  blobImage: Blob;
  modalSubscription: Subscription;
  dialogRef: any;

  readonly MAX_UPLOAD_IMAGE_SIZE = 125000; // Image Upload size 125000 byte = 125kb
  public readonly MAX_NAME_LENGTH = 100;
  public readonly MIN_NAME_LENGTH = 3;
  public readonly MAX_DESCRIPTION_LENGTH = 240;
  public readonly MAX_LOCATION_LENGTH = 100;
  selectedOrgImage: string | ArrayBuffer | null;
  defaultTenantLogoBase64 =
    'iVBORw0KGgoAAAANSUhEUgAAABwAAAAYBAMAAAAbsoCxAAAAIVBMVEUAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAD///9tb27TAAAACnRSTlMAHCs5R1VWZHKAExTmNQAAAAFiS0dECmjQ9FYAAABZSURBVBjTY9CcCQYMUIDOnZ42sy0DwZ3EMDOBDScXojgNDBJhRkHAJAwukl5NTO4UpZlF6jOVBBiVwFyotgCgCixcNMX4TcZvFNQLBsxpRBiF5N/KSfj9CwBprHIx2CdTGAAAAABJRU5ErkJggg==';
  tenantNameErrorMsgs: {
    required: string;
    maxlength: string;
    minlength: string;
  };
  tenantLocationErrorMsgs: {
    maxlength: string;
  };
  tenantDescriptionErrorMsgs: {
    maxlength: string;
  };
  userData: UserPreferences;
  isPrivateDomain: boolean = true;
  orgVisibilityCheckboxLabel: string = '';
  newTenantName: string = '';
  userLoaded$ = this.userDataFacade.userData$.pipe(
    tap((userData) => {
      this.userData = userData;
      this.isPrivateDomain = this.userData?.hasOrgEmailProvider;
      this.activateFormGroup();
    }),
  );
  parentTenantId: string;
  parentTenantName: string;
  selectedTenant$ = this.userDataFacade.selectedTenant$.pipe(
    tap((selectedTenant) => {
      this.parentTenantId = selectedTenant?.id;
      this.parentTenantName = selectedTenant?.name;
    }),
  );
  newTenantCreated$ = this.userDataFacade.newTenantCreated$.pipe(
    tap(() => {
      this.openSuccessDialog();
      this.userDataFacade.resetTenantCreated();
    }),
  );

  $currentTenantId = toSignal(this.userDataFacade.currentTenantId$);

  trialServices: string[] = [];
  getApps$ = this.controlPageFacade.getApps$.pipe(
    tap((apps: ApplicationCard[]) => {
      const appsWithTrials = apps.filter((app: ApplicationCard) => app.isDefaultTrialEnabled);
      this.trialServices = R.map(appsWithTrials, (app) => app.appId.toString());
    }),
  );

  nameOfOrgMaxlengthErrorMsg: string;
  nameOfOrgMinlengthErrorMsg: string;
  showThisOrgInPublicList: string;
  hideThisOrgInPublicList: string;
  onLangChange: Subscription;
  sanitizier: DomSanitizer;
  fileInput = [];
  idempotencyKey: string = uuidv4();

  // Translation Keys
  createOrgTitle: string = 'fthub.create_org_title';
  addOrgLogoText: string = 'fthub.add_org_logo_text';
  orgTitle: string = 'fthub.org_title';
  titleTooltip: string = 'fthub.title_tooltip';
  nameOfOrg: string = 'fthub.name_of_organization';
  orgFinishSetupMsg: string = 'fthub.org_finish_setup_msg';
  orgCreated: string = 'fthub.org-created';

  ngOnInit(): void {
    this.onLangChange = this.translate.onLangChange.pipe(startWith(undefined)).subscribe(() => {
      this.setTranslatedText();
    });
  }

  setTranslatedText(): void {
    this.tenantNameErrorMsgs = {
      required: this.translate.instant('fthub.name_of_org_required_error_msg', { minlength: this.MIN_NAME_LENGTH }),
      maxlength: this.translate.instant('fthub.name_of_org_maxlength_error_msg', { maxlength: this.MAX_NAME_LENGTH }),
      minlength: this.translate.instant('fthub.name_of_org_minlength_error_msg', { minlength: this.MIN_NAME_LENGTH }),
    };
    this.tenantLocationErrorMsgs = {
      maxlength: this.translate.instant('fthub.location_maxlength_error_msg', {
        maxlength: this.MAX_LOCATION_LENGTH,
      }),
    };
    this.tenantDescriptionErrorMsgs = {
      maxlength: this.translate.instant('fthub.description_maxlength_error_msg', {
        maxlength: this.MAX_DESCRIPTION_LENGTH,
      }),
    };
    if (this.isSubOrgCreation) {
      this.createOrgTitle = 'fthub.create_sub_org_title';
      this.addOrgLogoText = 'fthub.add_sub_org_logo_text';
      this.orgTitle = 'fthub.sub_org_title';
      this.titleTooltip = 'fthub.sub_org_title_tooltip';
      this.nameOfOrg = 'fthub.name_of_sub_organization';
      this.orgFinishSetupMsg = 'fthub.sub_org_finish_setup_msg';
      this.orgCreated = 'fthub.sub-org-created';
    }
  }

  activateFormGroup(): void {
    const userEmailDomain = CommonConstants.getEmailDomain(this.userData?.email);
    this.orgVisibilityCheckboxLabel = `Allow people affiliated with your company (email@${userEmailDomain}) to find and request access to this organization`;

    this.createTenantForm = this.formBuilder.group({
      location: [''],
      tenantDescription: [''],
      tenantEmailDomain: [
        {
          disabled: true,
          value: userEmailDomain,
        },
      ],
      tenantName: ['', [Validators.required, Validators.minLength(3), minCharacters(3)]],
      tenantOwnerName: [
        {
          disabled: true,
          value: this.userData?.name,
        },
      ],
      visibility: [this.isPrivateDomain],
    });
    this.changeDetectorRef.detectChanges();
  }

  onSelectFiles(event): void {
    if (event[0]) {
      const file: File = event[0].file;
      const pattern = /\.(jpg|jpeg|png)$/i;
      if (!pattern.test(file.name)) {
        this.uiStateFacade.displayMessage({
          message: 'The selected filetype is not supported. Please select an image file (.png, .jpg, .jpeg*).',
          type: 'Error',
        });
        return;
      }
      if (file?.size > this.MAX_UPLOAD_IMAGE_SIZE) {
        this.uiStateFacade.displayMessage({
          message: 'The selected file is too large. Please select an image smaller than  125kB.',
          type: 'Error',
        });
        return;
      }
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = (): void => {
        const base64Data = reader.result;
        this.selectedOrgImage = reader.result;
        this.changeDetectorRef.detectChanges();
        const blob = this.getDataURItoBlob(base64Data);
        this.blobImage = blob;
      };
    }
  }
  getDataURItoBlob(dataURI): Blob {
    const byteString = atob(dataURI.split(',')[1]);
    const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];
    const bufferedArray = new ArrayBuffer(byteString.length);
    const fileBufferArray = new Uint8Array(bufferedArray);
    for (let i = 0; i < byteString.length; i += 1) {
      fileBufferArray[i] = byteString.charCodeAt(i);
    }
    return new Blob([bufferedArray], { type: mimeString });
  }

  createOrg(): void {
    const createTenantFormRequestBody: CreateTenant = {
      description: this.createTenantForm.value.tenantDescription,
      location: this.createTenantForm.value.location,
      name: this.createTenantForm.value.tenantName,
      visibility: this.createTenantForm.value.visibility ? DOMAINS.PUBLIC : DOMAINS.PRIVATE,
      trialServices: this.trialServices,
    };
    this.newTenantName = this.createTenantForm.value.tenantName;
    this.userDataFacade.createTenant(
      createTenantFormRequestBody,
      this.blobImage || this.defaultTenantLogoBase64,
      this.idempotencyKey,
    );
  }

  createSubOrg(): void {
    const createChildTenantFormRequestBody: CreateChildTenant = {
      description: this.createTenantForm.value.tenantDescription,
      location: this.createTenantForm.value.location,
      name: this.createTenantForm.value.tenantName,
      parent: this.parentTenantId,
      trialServiceKinds: this.trialServices,
    };
    this.newTenantName = this.createTenantForm.value.tenantName;
    this.userDataFacade.createChildTenant(
      createChildTenantFormRequestBody,
      this.blobImage || this.defaultTenantLogoBase64,
      this.idempotencyKey,
      this.parentTenantName,
    );
  }

  cancelOrgCreation(): void {
    if (this.createTenantForm.dirty || this.blobImage) {
      const config: IDialogConfig = {
        title: this.translate.instant('fthub.discard_changes'),
        message: this.translate.instant('fthub.do_you_want_to_proceed'),
        messageType: NotificationType.Warning,
        buttons: [
          {
            label: this.translate.instant('fthub.continue'),
            buttonStyle: ActionButtonStyles.Main,
            shouldAutofocus: true,
          },
          {
            label: this.translate.instant('fthub.cancel'),
          },
        ],
      };

      this.modalSubscription = this.openDialogWrapper$(this.dialogSvc, config).subscribe((event: any) => {
        if (event.label === this.translate.instant('fthub.continue')) {
          const redirectTo = this.isSubOrgCreation
            ? `${TENANT_MGMT_ROUTES.ORGANIZATIONS}/${this.parentTenantId}/suborgs`
            : TENANT_MGMT_ROUTES.ORGANIZATIONS;
          this.router.navigate([redirectTo]);
        }
      });
    } else {
      const redirectTo = this.isSubOrgCreation
        ? `${TENANT_MGMT_ROUTES.ORGANIZATIONS}/${this.parentTenantId}/suborgs`
        : TENANT_MGMT_ROUTES.ORGANIZATIONS;
      this.router.navigate([redirectTo]);
    }
  }

  getInputHint(inputLength, validLength): string {
    return inputLength.length + '/' + validLength;
  }

  orgImageError(): void {
    this.selectedOrgImage = 'assets/images/org-logo-blank.svg';
  }

  openSuccessDialog(): void {
    const config: IDialogConfig = {
      title: this.translate.instant(this.orgCreated),
      message: '',
      hideTitleIcon: true,
      showCloseIconButton: true,
      contentProjection: { content: this.orgCreatedSuccess },
      buttons: [],
    };
    this.dialogRef = this.dialogSvc.openDialog(config);
    this.modalSubscription = this.dialogRef.afterClosed().subscribe((event: any) => {
      if (event === 'AllocateEntitlements') {
        this.router.navigate([`${TENANT_MGMT_ROUTES.ORGANIZATIONS}/${this.$currentTenantId()}/entitlement`]);
      } else if (event === 'InviteUsers') {
        this.router.navigate([`${TENANT_MGMT_ROUTES.ORGANIZATIONS}/${this.$currentTenantId()}/invite-users`]);
      } else if (!event) {
        this.router.navigate([`${TENANT_MGMT_ROUTES.ORGANIZATIONS}/${this.$currentTenantId()}/dashboard`]);
      }
    });
  }

  allocateEntitlements(): void {
    this.dialogRef.close('AllocateEntitlements');
  }
  inviteUsers(): void {
    this.dialogRef.close('InviteUsers');
  }
  closeSuccessDialog(): void {
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    this.onLangChange?.unsubscribe();
    this.modalSubscription?.unsubscribe();
  }
}
